// ── Photo upload ────────────────────────────────────────────────────────────────
// There is no object store in this stack — images are held as base64 data URLs in Postgres TEXT
// columns. That works, but every byte travels in every GraphQL response that includes the field, so
// the resize below is not cosmetic: it is what keeps a group listing from becoming megabytes.
//
// Extracted from the product-photo uploader in GroupDetail so every photo field shares one
// implementation and one size ceiling.

// Roughly 4/3 after base64 expansion, so keep the encoded ceiling comfortably under what the server
// accepts (see MAX_IMAGE_CHARS in server/resolvers.js).
export const MAX_SOURCE_BYTES = 10 * 1024 * 1024;   // reject huge originals before decoding
export const MAX_STORED_CHARS = 400 * 1024;          // ~400 KB of data URL

// Sensible defaults per use. Covers and logos are displayed small, so they don't need to be large.
export const IMAGE_PRESETS = {
  cover:   { maxPx: 640, quality: 0.82 },  // group cover art
  product: { maxPx: 480, quality: 0.82 },  // shop listing
  logo:    { maxPx: 320, quality: 0.85 },  // company logo
  post:    { maxPx: 900, quality: 0.80 },  // social feed photo — the one people actually look at
  avatar:  { maxPx: 256, quality: 0.85 },
  // A UPI QR has to survive being re-scanned off a screen, so it keeps more pixels and a higher
  // quality than a photo would need — JPEG ringing on the module edges is what breaks a decode.
  qr:      { maxPx: 720, quality: 0.92 },
};

// Downscale to fit maxPx on the long edge and re-encode as JPEG. Never upscales.
export function resizeImageToDataUrl(file, maxPx = 480, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const src = URL.createObjectURL(file);
    img.onload = () => {
      const ratio = Math.min(maxPx / img.width, maxPx / img.height, 1);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(src);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => { URL.revokeObjectURL(src); reject(new Error('That file could not be read as an image')); };
    img.src = src;
  });
}

// Pick a file, validate it, and return a data URL — or throw with a message worth showing the user.
// Re-encodes at progressively lower quality if the first pass is still over the ceiling, rather than
// failing on a photo the user reasonably expects to work.
export async function fileToStoredImage(file, preset = 'cover') {
  if (!file) throw new Error('No file chosen');
  if (!file.type?.startsWith('image/')) throw new Error('Please choose an image file');
  if (file.size > MAX_SOURCE_BYTES) throw new Error('That image is over 10 MB — please choose a smaller one');

  const { maxPx, quality } = IMAGE_PRESETS[preset] || IMAGE_PRESETS.cover;
  let dataUrl = await resizeImageToDataUrl(file, maxPx, quality);

  // Busy photos compress badly; step down instead of rejecting.
  for (const [px, q] of [[maxPx, 0.7], [Math.round(maxPx * 0.75), 0.65], [Math.round(maxPx * 0.5), 0.6]]) {
    if (dataUrl.length <= MAX_STORED_CHARS) break;
    dataUrl = await resizeImageToDataUrl(file, px, q);
  }
  if (dataUrl.length > MAX_STORED_CHARS) {
    throw new Error('That image is too detailed to store — try a simpler or smaller photo');
  }
  return dataUrl;
}

// Accept only http(s) or an image data URL, so a pasted value can't smuggle javascript: into an
// attribute that gets rendered as an image or a link.
export function isSafeImageValue(value) {
  const v = String(value || '').trim();
  if (!v) return true; // empty means "no image", which is always fine
  if (/^data:image\/(png|jpe?g|gif|webp);base64,/i.test(v)) return v.length <= MAX_STORED_CHARS;
  return /^https:\/\//i.test(v) || /^http:\/\//i.test(v);
}
